import React from 'react';
import styled from 'styled-components';

import Dropdown from '../Dropdown';
import Row from './Row';

import {RoundButton} from '../../../../components/UI';
import ProgressBar from '../../../../components/ProgressBar';
import {H5} from '../../../../components/Typography';
import {Row as FlexRow} from '../../../../components/Grid';
import {getTestBlockConnections, getRuntime} from '@bootcamp/shared/src/util';
import {useUserDataContext} from '../../../../contexts/UserData';
import moment from 'moment';
import {Info} from '@styled-icons/fluentui-system-filled/Info';
import {SubmitAnatomyCase} from '../../../../components/Modal';
import {useModalContext} from '../../../../contexts/Modal';
import API, {graphqlOperation} from '@aws-amplify/api';
import theme from '@bootcamp/shared/src/styles/theme';
import {Button} from '@bootcamp/web/src/components/Branding/Buttons';
import {Label2} from '../../../../components/Typography/next';


const AlertIcon = styled(Info)`
  color: ${({theme}) => theme.colors.interfacePalette.red.default};
`;
const AlertContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
  background-color: ${({theme}) => theme.colors.interfacePalette.red.default}3A;
  padding: 12px;
  width: fit-content;
  height: auto;
  margin: 0 auto;
  margin-bottom: 16px;
`;

const Details = styled(H5)`
  color: ${({theme}) => theme.colors.neutralsPalette.grey};
  margin: 0 !important;
  line-height: 21px;
`;
const Progress = styled(ProgressBar)`
  max-width: 630px;

  ${({theme}) => theme.mediaQueries.mobileL} {
    align-items: flex-start;
    margin: ${({theme}) => theme.layouts.spacing.m} 0 0;
  }
`;
const CTAWrapper = styled(FlexRow)`
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 8px 0 16px;

`;

const ProgressBarWrapper = styled.div`
  margin-top: 8px;
  width: 100%;
  ${({hidden}) => hidden && `visibility: hidden;`}
  ${({theme}) => theme.mediaQueries.mobileL} {
    margin-top: 8px;
  }
`;
const StyledButton = styled(Button)`
  min-width: 98px;

  ${({theme}) => theme.mediaQueries.tablet} {
    width: 50%;
  }

  ${theme.mediaQueries.mobileL} {
    height: 32px;
  }
`;
const StyledRoundButton = styled(Button)`
  min-width: 98px;
  height: 42px;

  ${({theme}) => theme.mediaQueries.tablet} {
    width: 50%;

    display: ${({attempted}) => attempted ? 'flex' : 'none'};
  }
  padding: 0;

  ${theme.mediaQueries.mobileL} {
    height: 32px;
  }
`;
const RuntimeText = styled.span`
  display: inline-block;

  ${({theme}) => theme.mediaQueries.mobileL} {
    display: none;
  }
`;
const ProgressLabel = styled(Label2)`
  color: ${({theme}) => theme.colors.typographyPalette.secondary};
`;
const Dot = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  margin: 0 4px;
  text-align: center;
  color: ${({theme}) => theme.colors.iconPalette.tertiary};
`;


const VideoLesson = props => {
  const {config: {route, name, id, section}, videoLessonData: {test, quizProgress, progressId}, refreshVideoLessonData, saveBlockProgress, searchTerm, themePalette, locked, chapterIndex, classroomName, displayIndex} = props;
  const {isUpgraded, bootcamp} = useUserDataContext();
  // if (!test || !quizProgress) return null;
  const {modalDispatch} = useModalContext();

  // if (!test || !quizProgress) return <LoadingContainer children={<LoadingSpinner color={'white'} size={24} />} />
  const testBlockConnections = getTestBlockConnections(test) || [];
  const testBlocks = testBlockConnections.reduce((acc, {testBlock}) => testBlock ? [...acc, testBlock] : acc, []);

  const completed = quizProgress?.reduce((acc, blockProgress) => blockProgress.status === 'complete' ? acc + 1 : acc, 0) || 0
  const total = testBlocks.filter(testBlock => !['startBlock', 'endBlock'].includes(testBlock.type)).length

  const activeBlock = quizProgress?.findIndex(blockProgress => blockProgress.status === '');

  function isFilteredBlock (testBlock) {
    if (['startBlock', 'endBlock'].includes(testBlock.type) || !testBlock.title) return false;
    if (!searchTerm) return true;
    const testBlockKeywords = testBlock.components.find(({renderType}) => renderType === 'keywords')?.contents || '';
    return testBlock.title.toLowerCase().includes(searchTerm.toLowerCase()) || name.toLowerCase().includes(searchTerm.toLowerCase()) || searchTerm.toLowerCase().split(' ').every(key => testBlockKeywords.toLowerCase().includes(key));
  }

  if (testBlocks.length > 0 && !testBlocks.some(testBlock => isFilteredBlock(testBlock))) {
    return null;
  }
  const targetIndex = searchTerm ? testBlocks.findIndex(testBlock => isFilteredBlock(testBlock)) : activeBlock > -1 ? activeBlock : 1;
  const blockLink = locked ? '#' : `videos/${route}?index=${targetIndex}`;

  const urlParams = new URLSearchParams(window.location.search);
  const selectedSection = urlParams.get('section');
  if (selectedSection && selectedSection !== 'all' && selectedSection !== section) return null;
  const unlockLimit = 3;
  const displayLimit = ['Quantitative Reasoning'].includes(classroomName) ? 1 : 0;
  const rows = testBlocks.reduce((acc, testBlock, index) =>
    isFilteredBlock(testBlock)
      ? [
        ...acc,
          <Row
            index={index}
            key={testBlock.id}
            testBlock={testBlock}
            blockProgress={quizProgress[index]}
            classroomName={classroomName}
            saveBlockProgress={(value, blockIndex) => saveBlockProgress(id, value, blockIndex)}
            themePalette={themePalette}
            route={route}
            locked={bootcamp === 'mcat' ? !isUpgraded : bootcamp === 'nclex' ? false : ['dat', 'oat'].includes(bootcamp) && (displayIndex <= displayLimit || (displayIndex < unlockLimit && ['Biology', 'General Chemistry', 'Organic Chemistry'].includes(classroomName))) ? false : (bootcamp !== 'inbde' && !isUpgraded && index > 2 && !window.location.pathname.includes('application-services'))}
            bootcamp={bootcamp}
          />
      ] : acc, []);
  const ResetButton = completed > 0 && (
    <StyledButton
      colorConfig={themePalette}
      round
      type="ghost"
      children={'Reset'}
      size={'small'}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        modalDispatch({
          type: 'open',
          component: SubmitAnatomyCase,
          componentProps: {
            icon: <AlertContainer><AlertIcon size={32} /></AlertContainer>,
            textAlign: 'center',
            headerText: 'Reset this video lesson?',
            bodyText: 'This will reset all progress for this lesson. Your tagged questions will remain tagged.',
            confirmConfig: {
              onConfirm: async () => {
                await API.graphql(graphqlOperation(`
                  mutation UpdateTestProgress($input: UpdateTestProgressInput!) {
                    updateTestProgress(input: $input) {
                      id
                    }
                  }`, {input: {id: progressId, didReset: true}}))
                await refreshVideoLessonData(id);
              },
              colorConfig: theme.colors.interfacePalette.red,
              text: 'Yes, reset this video lesson'
            },
            cancelConfig: {
              text: 'No, don\'t reset'
            },
          },
          enableClickClose: true,
          modalContainerStyle: theme => `
            background: ${theme.overlays.opacity.dark._300};
            padding: 10vh 10vw;
            align-items: flex-start;
            ${theme.mediaQueries.tablet} {
              background: ${theme.overlays.opacity.dark._400};
              padding: 0;
            }
          `
      })
      return false;
    }}>
      Reset
    </StyledButton>
  )
  return (
    <Dropdown
      route={blockLink}
      name={name}
      index={chapterIndex}
      expandOnChange={searchTerm}
      textContent={(
        <ProgressBarWrapper>
          <Progress
            loading={!test}
            segments={[{count: completed, color: themePalette?.gradient || 'white'}]}
            total={total}
            label={<ProgressLabel><strong>{completed > total ? total : completed}</strong>{` of ${total} lessons completed`}<RuntimeText><Dot>&#x2022;</Dot>{getRuntime(testBlocks)}</RuntimeText></ProgressLabel>}
            lightMode
          />
          <Details>
          </Details>
        </ProgressBarWrapper>
      )}
      actions={[
        <CTAWrapper>
          {ResetButton}
          <StyledRoundButton size={"medium"} round colorConfig={themePalette} attempted={completed > 0}>
            {bootcamp === 'mcat' && !isUpgraded ? 'Upgrade' : completed > 0 ? 'Resume' : 'Start'}
          </StyledRoundButton>
        </CTAWrapper>
      ]}
      {...props}
      locked={(bootcamp === 'mcat' && !isUpgraded) || locked}
    >
      {rows}
    </Dropdown>
  );
};

export default VideoLesson;
